import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Zap, Repeat, Flag, Users, ToggleLeft, ToggleRight, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";

// Règles V1 structurées — exécutées côté serveur (boucle 5 min + pipeline d'analyse)
const RULES = [
  { type: "lead_no_reply", icon: Repeat, titleKey: "ruleNoReply", descKey: "ruleNoReplyDesc", hasDays: true },
  { type: "important_task", icon: Flag, titleKey: "ruleImportant", descKey: "ruleImportantDesc", hasDays: false },
  { type: "new_lead", icon: Users, titleKey: "ruleNewLead", descKey: "ruleNewLeadDesc", hasDays: false },
];
// Défauts alignés sur le backend : new_lead active par défaut (comportement historique)
const DEFAULTS = {
  lead_no_reply: { active: false, days: 4 },
  important_task: { active: false, days: 0 },
  new_lead: { active: true, days: 0 },
};

export default function Automatisations() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/automations").then((r) => setItems(r.data.automations)).catch(() => {});
  useEffect(() => { load(); }, []);

  const docFor = (type) => (items || []).find((a) => a.type === type);
  const cfgFor = (type) => {
    const doc = docFor(type);
    const def = DEFAULTS[type];
    if (!doc) return { ...def };
    return { active: !!doc.active, days: Math.max(1, Math.min(30, (doc.params || {}).days || def.days || 4)) };
  };

  const saveRule = async (type, patch) => {
    setBusy(type);
    try {
      const doc = docFor(type);
      if (doc) {
        await api.patch(`/automations/${doc.automation_id}`, patch);
      } else {
        await api.post("/automations", {
          name: t(`automatisations.${RULES.find((r) => r.type === type).titleKey}`),
          type, params: { days: cfgFor(type).days }, active: false, ...patch,
        });
      }
      toast.success(t("automatisations.saved"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy("");
  };

  const legacy = (items || []).filter((a) => !a.type);

  return (
    <div className="space-y-8 max-w-4xl" data-testid="automatisations-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter flex items-center gap-3">
          <Zap size={26} className="text-electric" /> {t("automatisations.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">{t("automatisations.subtitle")}</p>
      </div>

      <div className="space-y-4">
        {items === null ? <div className="h-32 rounded-2xl bg-card animate-pulse" /> : RULES.map((rule, i) => {
          const cfg = cfgFor(rule.type);
          return (
            <motion.div key={rule.type}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className={`rounded-2xl border p-6 transition-colors ${cfg.active ? "border-electric/60 bg-electric/5" : "border-border bg-card"}`}
              data-testid={`automation-rule-${rule.type}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${cfg.active ? "bg-electric/15" : "bg-secondary"}`}>
                    <rule.icon size={18} className={cfg.active ? "text-electric" : "text-muted-foreground"} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h3 className="font-display font-bold">{t(`automatisations.${rule.titleKey}`)}</h3>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.active ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`}
                        data-testid={`automation-status-${rule.type}`}>
                        {cfg.active ? t("automatisations.active") : t("automatisations.inactive")}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-xl">
                      {t(`automatisations.${rule.descKey}`)}
                    </p>
                  </div>
                </div>
                <button onClick={() => saveRule(rule.type, { active: !cfg.active })}
                  disabled={busy === rule.type}
                  data-testid={`automation-toggle-${rule.type}`}
                  className="shrink-0 text-muted-foreground hover:text-electric transition-colors disabled:opacity-50"
                  aria-label={cfg.active ? t("automatisations.inactive") : t("automatisations.active")}>
                  {cfg.active ? <ToggleRight size={34} className="text-electric" /> : <ToggleLeft size={34} />}
                </button>
              </div>
              {rule.hasDays && (
                <div className="mt-4 ml-14 flex items-center gap-3 text-sm">
                  <Clock size={14} className="text-muted-foreground" />
                  <label htmlFor={`days-${rule.type}`} className="text-muted-foreground">{t("automatisations.daysLabel")}</label>
                  <select id={`days-${rule.type}`} value={cfg.days}
                    onChange={(e) => saveRule(rule.type, { params: { days: parseInt(e.target.value, 10) } })}
                    disabled={busy === rule.type}
                    data-testid={`automation-days-${rule.type}`}
                    className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-electric disabled:opacity-50">
                    {Array.from({ length: 30 }, (_, n) => n + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span className="text-muted-foreground">{t("automatisations.daysUnit")}</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {legacy.length > 0 && (
        <div className="space-y-3" data-testid="automation-legacy-list">
          <h2 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("automatisations.legacyTitle")}</h2>
          {legacy.map((a) => (
            <div key={a.automation_id}
              className="rounded-xl border border-dashed border-border bg-secondary/30 px-5 py-4 flex items-start justify-between gap-4"
              data-testid={`automation-legacy-${a.automation_id}`}>
              <div className="text-sm">
                <p className="font-semibold">{a.name}</p>
                <p className="text-muted-foreground text-xs mt-1">
                  <span className="text-electric font-semibold">{t("automatisations.if", { defaultValue: "SI" })}</span> {a.when_condition}
                  <span className="text-success font-semibold"> {t("automatisations.then", { defaultValue: "ALORS" })}</span> {a.then_action}
                </p>
                <p className="text-[11px] text-muted-foreground/70 italic mt-1.5" data-testid="automation-legacy-note">
                  {t("automatisations.legacyNote")}
                </p>
              </div>
              <button onClick={async () => { await api.delete(`/automations/${a.automation_id}`); load(); }}
                data-testid={`automation-legacy-delete-${a.automation_id}`}
                className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
